import { formatLocalDate } from '@/utils/localDateUtils';
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FactorReajuste } from '@/types';
import adjustmentService from '@/services/adjustmentService';
import MainLayout from '@/components/layout/MainLayout';
import Button from '@/components/common/Button';
import Modal from '@/components/common/Modal';
import { useDialog } from '@/hooks/useDialog';

interface EligibleContract {
  id: number;
  numero_contrato: string;
  inmueble_direccion: string;
  inquilino_nombre: string;
  monto_alquiler_inquilino: number;
  monto_alquiler_propietario: number;
  moneda_inquilino: string;
  moneda_propietario: string;
  fecha_inicio: string;
  meses_transcurridos: number;
}

interface ExtendedFactorReajuste extends FactorReajuste {
  aplicado?: boolean;
  fecha_aplicacion?: string;
  contratos_afectados?: number;
}

export default function AdjustmentFactorDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showAlert, showConfirm } = useDialog();
  const [factor, setFactor] = useState<ExtendedFactorReajuste | null>(null);
  const [loading, setLoading] = useState(true);
  const [eligibleContracts, setEligibleContracts] = useState<EligibleContract[]>([]);
  const [loadingContracts, setLoadingContracts] = useState(false);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [showRevertModal, setShowRevertModal] = useState(false);
  const [applying, setApplying] = useState(false);
  const [reverting, setReverting] = useState(false);
  const [applyResult, setApplyResult] = useState<any>(null);

  useEffect(() => {
    (async () => {
      try {
        if (!id) return;
        const res = await adjustmentService.getAdjustmentFactorById(Number(id));
        const factorData = (res as any).data || res;
        setFactor(factorData);

        // Load eligible contracts
        if (factorData && !factorData.aplicado) {
          loadEligibleContracts(factorData.anio, factorData.mes);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const loadEligibleContracts = async (year: number, month: number) => {
    try {
      setLoadingContracts(true);
      console.log(`[Reajuste] Loading eligible contracts for year=${year}, month=${month}`);
      const res = await adjustmentService.getContractsForAdjustment(year, month);
      console.log('[Reajuste] API response:', res);
      const data = (res as any).data || res;
      console.log('[Reajuste] Parsed data:', data);
      // API returns { contracts: [...], total: N }
      const contracts = data.contracts || data;
      console.log('[Reajuste] Contracts:', contracts);
      setEligibleContracts(Array.isArray(contracts) ? contracts : []);
    } catch (error) {
      console.error('Error loading eligible contracts:', error);
      setEligibleContracts([]);
    } finally {
      setLoadingContracts(false);
    }
  };

  const monthNames = [
    '', 'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '-';
    return formatLocalDate(dateString, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPercentage = (value: number) => {
    return (Number(value) * 100).toFixed(2);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-UY', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const calculateNewAmount = (original: number, factorValue: number) => {
    const f = Number(factorValue);
    return original * (1 + f);
  };

  const calculateIncrement = (original: number, factorValue: number) => {
    const f = Number(factorValue);
    return original * f;
  };

  const handleDelete = async () => {
    if (!factor) return;
    
    const confirmed = await showConfirm({
      title: 'Eliminar Factor',
      message: '¿Está seguro que desea eliminar este factor de reajuste?',
      type: 'danger',
      confirmText: 'Sí, eliminar',
    });
    if (!confirmed) return;

    try {
      await adjustmentService.deleteAdjustmentFactor(Number(factor.id));
      navigate('/adjustments');
    } catch (error) {
      console.error('Error deleting adjustment factor:', error);
      showAlert({
        type: 'error',
        title: 'Error',
        message: 'Error al eliminar el factor de reajuste',
      });
    }
  };

  const handleApply = async () => {
    if (!factor) return;

    try {
      setApplying(true);
      const res = await adjustmentService.applyAdjustmentFactor(factor.anio, factor.mes);
      const result = (res as any).data || res;
      setApplyResult(result);

      // Reload factor to get updated status
      const factorRes = await adjustmentService.getAdjustmentFactorById(Number(id));
      setFactor((factorRes as any).data || factorRes);
      setEligibleContracts([]);
    } catch (error: any) {
      console.error('Error applying adjustment:', error);
      showAlert({
        type: 'error',
        title: 'Error',
        message: error.response?.data?.message || 'Error al aplicar el reajuste',
      });
    } finally {
      setApplying(false);
    }
  };

  const handleRevert = async () => {
    if (!factor) return;

    try {
      setReverting(true);
      await adjustmentService.revertAdjustment(Number(factor.id));
      showAlert({
        type: 'success',
        title: 'Éxito',
        message: 'Reajuste revertido exitosamente',
      });
      setShowRevertModal(false);

      // Reload factor to get updated status
      const factorRes = await adjustmentService.getAdjustmentFactorById(Number(id));
      const factorData = (factorRes as any).data || factorRes;
      setFactor(factorData);

      // Reload eligible contracts
      loadEligibleContracts(factorData.anio, factorData.mes);
    } catch (error: any) {
      console.error('Error reverting adjustment:', error);
      showAlert({
        type: 'error',
        title: 'Error',
        message: error.response?.data?.message || 'Error al revertir el reajuste',
      });
    } finally {
      setReverting(false);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="p-6 max-w-7xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
            <div className="h-96 bg-gray-200 rounded"></div>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (!factor) {
    return (
      <MainLayout>
        <div className="p-6 max-w-7xl mx-auto">
          <div className="text-center py-12">
            <div className="text-6xl mb-4">📈</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Factor de reajuste no encontrado</h3>
            <Button onClick={() => navigate(-1)}>Volver</Button>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl font-bold text-gray-900">
                Factor de Reajuste {monthNames[factor.mes]} {factor.anio}
              </h1>
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-lg font-bold border-2 bg-blue-50 text-blue-700 border-blue-300">
                <span>📊</span>
                {formatPercentage(factor.valor)}%
              </span>
              {/* Application Status Badge */}
              {factor.aplicado ? (
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 border border-green-300">
                  ✓ Aplicado
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800 border border-yellow-300">
                  ⏳ Pendiente
                </span>
              )}
            </div>
            <p className="text-gray-600 flex items-center gap-2">
              <span>📅</span>
              Contratos que vencen en {monthNames[factor.mes]} {factor.anio}
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => navigate(-1)}>
              Volver
            </Button>
            <Button onClick={() => navigate(`/adjustments/${id}/edit`)}>
              Editar Factor
            </Button>
          </div>
        </div>

        {/* Application Status Alert */}
        {!!factor.aplicado && factor.fecha_aplicacion && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="text-2xl">✅</div>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-green-900">Reajuste Aplicado</h3>
                <p className="text-sm text-green-700">
                  Aplicado el {formatDate(factor.fecha_aplicacion)}
                  {factor.contratos_afectados !== undefined && (
                    <span> • {factor.contratos_afectados} contratos afectados</span>
                  )}
                </p>
              </div>
              <Button
                variant="danger"
                size="sm"
                onClick={() => setShowRevertModal(true)}
              >
                Revertir
              </Button>
            </div>
          </div>
        )}

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Factor Details Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <span>📈</span>
                Detalles del Factor
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">Período del Factor</h3>
                  <p className="text-gray-900 font-medium text-lg">
                    {monthNames[factor.mes]} {factor.anio}
                  </p>
                </div>

                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">Valor del Ajuste</h3>
                  <div className="flex items-baseline gap-2">
                    <p className="text-3xl font-bold text-blue-600">
                      {formatPercentage(factor.valor)}%
                    </p>
                    <p className="text-sm text-gray-500">
                      (Decimal: {factor.valor})
                    </p>
                  </div>
                </div>

                <div className="md:col-span-2 pt-4 border-t border-gray-200">
                  <h3 className="text-sm font-medium text-gray-500 mb-1">Contratos correspondientes</h3>
                  <p className="text-gray-900 font-medium">
                    Este factor se usa para calcular las 12 mensualidades de la prórroga de los contratos que vencen en{' '}
                    <span className="text-blue-600 font-bold">
                      {monthNames[factor.mes]} {factor.anio}
                    </span>
                  </p>
                </div>
              </div>
            </div>

            {/* Calculation Example Card */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border border-blue-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <span>🧮</span>
                Ejemplo de Cálculo
              </h2>
              <div className="space-y-3 text-sm">
                <div className="bg-white rounded-lg p-4">
                  <p className="text-gray-700 mb-2">
                    Si un contrato tiene un monto de alquiler de:
                  </p>
                  <p className="text-2xl font-bold text-gray-900 mb-3">$10,000 UYU</p>

                  <div className="space-y-2 text-gray-700">
                    <div className="flex justify-between items-center">
                      <span>Monto original:</span>
                      <span className="font-semibold">${formatCurrency(10000)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span>Factor de ajuste ({formatPercentage(factor.valor)}%):</span>
                      <span className="font-semibold">× {(1 + Number(factor.valor)).toFixed(4)}</span>
                    </div>
                    <div className="border-t border-gray-300 pt-2 flex justify-between items-center">
                      <span className="font-bold">Nuevo monto:</span>
                      <span className="text-xl font-bold text-blue-600">
                        ${formatCurrency(calculateNewAmount(10000, factor.valor))}
                      </span>
                    </div>
                    <div className="flex justify-between items-center text-green-600">
                      <span>Incremento:</span>
                      <span className="font-bold">
                        +${formatCurrency(calculateIncrement(10000, factor.valor))}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="text-xs text-gray-600 bg-white rounded-lg p-3">
                  <p className="font-semibold mb-1">Fórmula:</p>
                  <p className="font-mono">Nuevo monto = Monto original × (1 + factor)</p>
                  <div className="mt-2 pt-2 border-t border-gray-300 text-gray-500">
                    <p className="font-semibold mb-1">Detalle de cálculo:</p>
                    <p className="font-mono">10,000 × (1 + {Number(factor.valor)}) = 10,000 × {(1 + Number(factor.valor)).toFixed(4)} = {calculateNewAmount(10000, factor.valor).toFixed(2)}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Eligible Contracts Card */}
            {!factor.aplicado && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <span>📋</span>
                    Contratos Elegibles para Reajuste
                  </h2>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => loadEligibleContracts(factor.anio, factor.mes)}
                    disabled={loadingContracts}
                  >
                    {loadingContracts ? 'Cargando...' : 'Actualizar'}
                  </Button>
                </div>

                {loadingContracts ? (
                  <div className="animate-pulse space-y-3">
                    <div className="h-16 bg-gray-100 rounded"></div>
                    <div className="h-16 bg-gray-100 rounded"></div>
                    <div className="h-16 bg-gray-100 rounded"></div>
                  </div>
                ) : eligibleContracts.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <div className="text-4xl mb-2">📭</div>
                    <p className="text-sm">
                      No hay contratos elegibles para este período de reajuste
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                      <p className="text-sm text-blue-800">
                        <strong>{eligibleContracts.length}</strong> contratos serán actualizados al aplicar este factor
                      </p>
                    </div>

                    <div className="max-h-96 overflow-y-auto space-y-2">
                      {eligibleContracts.map((contract) => (
                        <div
                          key={contract.id}
                          onClick={() => navigate(`/contracts/${contract.id}`)}
                          className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:bg-blue-50 cursor-pointer transition-all"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className="font-medium text-gray-900">{contract.numero_contrato}</p>
                              <p className="text-sm text-gray-600">{contract.inmueble_direccion}</p>
                              <p className="text-sm text-gray-500">Inquilino: {contract.inquilino_nombre}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-medium text-gray-900">
                                {contract.moneda_inquilino} {formatCurrency(contract.monto_alquiler_inquilino)}
                              </p>
                              <p className="text-xs text-green-600">
                                → {contract.moneda_inquilino} {formatCurrency(calculateNewAmount(contract.monto_alquiler_inquilino, factor.valor))}
                              </p>
                              <p className="text-xs text-gray-500">
                                {contract.meses_transcurridos} meses
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Apply Button */}
                    <div className="pt-4 border-t border-gray-200">
                      <Button
                        className="w-full"
                        onClick={() => setShowApplyModal(true)}
                      >
                        Aplicar Reajuste a {eligibleContracts.length} Contratos
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Applied Contracts Summary (when already applied) */}
            {!!factor.aplicado && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <span>📋</span>
                  Resumen de Aplicación
                </h2>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center gap-4">
                    <div className="text-4xl">✅</div>
                    <div>
                      <p className="text-green-900 font-medium">
                        Este reajuste ya fue aplicado
                      </p>
                      <p className="text-sm text-green-700">
                        {factor.contratos_afectados !== undefined ? (
                          <>{factor.contratos_afectados} contratos fueron actualizados</>
                        ) : (
                          <>Los contratos elegibles fueron actualizados</>
                        )}
                      </p>
                      {factor.fecha_aplicacion && (
                        <p className="text-sm text-green-600 mt-1">
                          Fecha: {formatDate(factor.fecha_aplicacion)}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Quick Stats & Actions */}
          <div className="space-y-6">
            {/* Quick Stats Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Resumen</h3>
              <div className="space-y-4">
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="text-center">
                    <p className="text-sm text-gray-600 mb-1">Ajuste Mensual</p>
                    <p className="text-3xl font-bold text-blue-600">
                      {formatPercentage(factor.valor)}%
                    </p>
                  </div>
                </div>

                {!factor.aplicado && eligibleContracts.length > 0 && (
                  <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                    <div className="text-center">
                      <p className="text-sm text-gray-600 mb-1">Contratos Pendientes</p>
                      <p className="text-2xl font-bold text-yellow-700">
                        {eligibleContracts.length}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Actions Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Acciones</h3>
              <div className="space-y-3">
                {!factor.aplicado && eligibleContracts.length > 0 && (
                  <Button
                    className="w-full bg-green-600 hover:bg-green-700"
                    onClick={() => setShowApplyModal(true)}
                  >
                    Aplicar Reajuste
                  </Button>
                )}
                {!!factor.aplicado && (
                  <Button
                    variant="danger"
                    className="w-full"
                    onClick={() => setShowRevertModal(true)}
                  >
                    Revertir Reajuste
                  </Button>
                )}
                <Button
                  className="w-full"
                  onClick={() => navigate(`/adjustments/${id}/edit`)}
                >
                  Editar Factor
                </Button>
                <Button
                  variant="secondary"
                  className="w-full"
                  onClick={() => navigate('/adjustments/new')}
                >
                  Crear Nuevo Factor
                </Button>
                <Button
                  variant="danger"
                  className="w-full"
                  onClick={handleDelete}
                  disabled={factor.aplicado}
                >
                  Eliminar Factor
                </Button>
                {!!factor.aplicado && (
                  <p className="text-xs text-gray-500 text-center">
                    No se puede eliminar un factor ya aplicado
                  </p>
                )}
              </div>
            </div>

            {/* Metadata Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Información del Sistema</h3>
              <div className="space-y-3 text-sm">
                <div>
                  <p className="text-gray-500">ID</p>
                  <p className="font-mono text-gray-900">#{factor.id}</p>
                </div>
                <div>
                  <p className="text-gray-500">Estado</p>
                  <p className={`font-medium ${factor.aplicado ? 'text-green-600' : 'text-yellow-600'}`}>
                    {factor.aplicado ? 'Aplicado' : 'Pendiente'}
                  </p>
                </div>
                {!!factor.definido_por && (
                  <div>
                    <p className="text-gray-500">Definido por</p>
                    <p className="text-gray-900">Usuario #{factor.definido_por}</p>
                  </div>
                )}
                {factor.created_at && (
                  <div>
                    <p className="text-gray-500">Creado</p>
                    <p className="text-gray-900">{formatDate(factor.created_at)}</p>
                  </div>
                )}
                {factor.fecha_aplicacion && (
                  <div>
                    <p className="text-gray-500">Aplicado</p>
                    <p className="text-gray-900">{formatDate(factor.fecha_aplicacion)}</p>
                  </div>
                )}
                {factor.updated_at && (
                  <div>
                    <p className="text-gray-500">Última actualización</p>
                    <p className="text-gray-900">{formatDate(factor.updated_at)}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Info Card */}
            <div className="bg-yellow-50 rounded-lg border border-yellow-200 p-4">
              <div className="flex gap-3">
                <div className="text-2xl">💡</div>
                <div>
                  <h4 className="font-semibold text-yellow-900 text-sm mb-1">Información</h4>
                  <p className="text-xs text-yellow-800">
                    Los factores de reajuste se basan en índices oficiales. Al aplicar, se actualizarán los montos de todos los contratos elegibles y sus próximos 12 pagos.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Apply Adjustment Modal */}
        <Modal
          isOpen={showApplyModal}
          onClose={() => {
            setShowApplyModal(false);
            setApplyResult(null);
          }}
          title="Aplicar Reajuste"
          size="md"
        >
          {applyResult ? (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <div className="text-3xl">✅</div>
                  <div>
                    <h3 className="font-semibold text-green-900">Reajuste Aplicado Exitosamente</h3>
                    <p className="text-sm text-green-700">
                      {applyResult.contracts_affected} contratos fueron actualizados
                    </p>
                    {applyResult.payments_affected !== undefined && (
                      <p className="text-sm text-green-600">
                        {applyResult.payments_affected} pagos fueron generados/actualizados
                      </p>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex justify-end">
                <Button onClick={() => {
                  setShowApplyModal(false);
                  setApplyResult(null);
                }}>
                  Cerrar
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  Al aplicar este reajuste, se actualizarán los montos de alquiler de{' '}
                  <strong>{eligibleContracts.length} contratos</strong> y se generarán/actualizarán
                  los próximos 12 pagos de cada contrato.
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Resumen del Reajuste</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Período:</span>
                    <span className="font-medium">{monthNames[factor.mes]} {factor.anio}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Factor:</span>
                    <span className="font-medium">{formatPercentage(factor.valor)}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Contratos a actualizar:</span>
                    <span className="font-medium">{eligibleContracts.length}</span>
                  </div>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-xs text-yellow-800">
                  <strong>Nota:</strong> Esta acción puede ser revertida posteriormente si es necesario.
                </p>
              </div>

              <div className="flex gap-3 justify-end pt-4">
                <Button
                  variant="secondary"
                  onClick={() => setShowApplyModal(false)}
                  disabled={applying}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleApply}
                  disabled={applying}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {applying ? 'Aplicando...' : 'Confirmar Aplicación'}
                </Button>
              </div>
            </div>
          )}
        </Modal>

        {/* Revert Adjustment Modal */}
        <Modal
          isOpen={showRevertModal}
          onClose={() => setShowRevertModal(false)}
          title="Revertir Reajuste"
          size="md"
        >
          <div className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800 font-medium">
                ⚠️ Precaución
              </p>
              <p className="text-sm text-red-700 mt-1">
                Al revertir este reajuste, todos los pagos que fueron actualizados volverán a sus montos anteriores.
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Información del Reajuste</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Período:</span>
                  <span className="font-medium">{monthNames[factor.mes]} {factor.anio}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Factor:</span>
                  <span className="font-medium">{formatPercentage(factor.valor)}%</span>
                </div>
                {factor.contratos_afectados !== undefined && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Contratos afectados:</span>
                    <span className="font-medium">{factor.contratos_afectados}</span>
                  </div>
                )}
                {factor.fecha_aplicacion && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Fecha de aplicación:</span>
                    <span className="font-medium">{formatDate(factor.fecha_aplicacion)}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-3 justify-end pt-4">
              <Button
                variant="secondary"
                onClick={() => setShowRevertModal(false)}
                disabled={reverting}
              >
                Cancelar
              </Button>
              <Button
                variant="danger"
                onClick={handleRevert}
                disabled={reverting}
              >
                {reverting ? 'Revirtiendo...' : 'Confirmar Reversión'}
              </Button>
            </div>
          </div>
        </Modal>
      </div>
    </MainLayout>
  );
}
